import { Injectable } from '@angular/core';
import { FiscalSellerRegistrationComponent } from '@common/components/fiscal-seller-registration/fiscal-seller-registration.component';
import { FiscalAccessService } from '@common/services/fiscal-access.service';
import { PlansService } from '@common/services/plans.service';
import { UserService } from '@modules/auth/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BehaviorSubject, firstValueFrom, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { FiscalPrinterData, FiscalSellerData } from '../models/fiscal-seller.model';
import { SmartImprFiscalPrinter } from '../models/smart-api.model';
import { SmartApiService } from './smart-api.service';

@Injectable({
    providedIn: 'root',
})
export class FiscalSellerStorageService {
    private readonly STORAGE_KEY_PREFIX = 'fina_fiscal_seller';
    private promptInProgress: Promise<FiscalSellerData | null> | null = null;
    readonly printerChanges$ = new BehaviorSubject<FiscalPrinterData | null>(null);

    constructor(
        private plansService: PlansService,
        private smartApiService: SmartApiService,
        private modalService: NgbModal,
        private userService: UserService,
        private fiscalAccess: FiscalAccessService,
    ) {}

    getCurrentSeller(): FiscalSellerData | null {
        const printer = this.getStoredPrinter();
        if (!printer) return null;

        return {
            codigo: printer.codigo,
            nombre: printer.nombre,
            numCajero: printer.numCajero,
        };
    }

    getStoredPrinter(): FiscalPrinterData | null {
        try {
            const raw = localStorage.getItem(this.getStorageKey());
            if (!raw) return null;

            const parsed = JSON.parse(raw);
            return this.normalizePrinter(parsed);
        } catch {
            this.clearSeller('invalid-storage');
            return null;
        }
    }

    saveSeller(seller: FiscalSellerData, active: boolean = true): void {
        const normalized = this.normalizePrinter({ ...seller, active });
        if (!normalized) return;

        localStorage.setItem(this.getStorageKey(), JSON.stringify(normalized));
        this.printerChanges$.next(normalized);
    }

    savePrinter(printer: FiscalSellerData, active: boolean = true): void {
        this.saveSeller(printer, active);
    }

    clearSeller(reason: string = 'manual'): void {
        console.log('[SMART][FISCAL] clearSeller', { reason, key: this.getStorageKey() });
        localStorage.removeItem(this.getStorageKey());
        this.printerChanges$.next(null);
    }

    async ensureRegisteredIfRequired(waitMs?: number): Promise<FiscalSellerData | null> {
        const integration = await this.getRequiredImprFiscalIntegration(waitMs, 'issue');
        if (!integration) return this.getCurrentSeller();

        const currentSeller = this.getCurrentSeller();
        const configuredPrinters = integration.data?.imprFiscalPrinters || [];
        const activePrinters = configuredPrinters.filter(printer => printer.enabled !== false);

        if (currentSeller && this.matchesConfiguredPrinter(currentSeller, configuredPrinters)) {
            return currentSeller;
        }

        // FINA-2753: no borrar la caja si el listado de impresoras activas no es confiable
        // (vacío, aún no sincronizado, o todas deshabilitadas). Solo limpiar ante mismatch inequívoco.
        if (currentSeller && activePrinters.length === 0) {
            console.warn('[SMART][FISCAL] Se conserva la caja fiscal guardada: no hay impresoras activas confiables desde la integración.', {
                numCajero: currentSeller.numCajero,
                codigo: currentSeller.codigo,
                configuredPrintersCount: configuredPrinters.length,
            });
            return currentSeller;
        }

        if (currentSeller) {
            console.warn('[SMART][FISCAL] Caja fiscal guardada no coincide con impresoras activas; se solicita reconfiguración.', {
                numCajero: currentSeller.numCajero,
                codigo: currentSeller.codigo,
                activePrinters: activePrinters.map(printer => ({ codigo: printer.codigo, numCajero: printer.numCajero })),
            });
            this.clearSeller('mismatch-with-active-printers');
        }

        if (!this.promptInProgress) {
            this.promptInProgress = this.openRegistrationModal(configuredPrinters).finally(() => {
                this.promptInProgress = null;
            });
        }

        return this.promptInProgress;
    }

    async isRequiredForCurrentTenant(waitMs?: number): Promise<boolean> {
        return !!(await this.getRequiredImprFiscalIntegration(waitMs, 'access'));
    }

    private async getRequiredImprFiscalIntegration(
        waitMs?: number,
        gate: 'access' | 'issue' = 'issue',
    ): Promise<{ success: boolean; data?: { scope?: string; imprFiscalPrinters?: SmartImprFiscalPrinter[] } } | null> {
        const planAllows = await this.currentPlanAllowsFiscalInvoice(waitMs, gate);
        if (!planAllows) return null;

        const integration = await firstValueFrom(
            this.smartApiService.getSmartIntegration().pipe(
                take(1),
                catchError(() => of(null)),
            ),
        );

        return integration?.data?.scope === 'imprFiscal' ? integration : null;
    }

    validateSeller(seller: Partial<FiscalSellerData> | null | undefined): seller is FiscalSellerData {
        return !!this.normalizeSeller(seller);
    }

    private normalizeSeller(seller: Partial<FiscalSellerData> | null | undefined): FiscalSellerData | null {
        const codigo = String(seller?.codigo || '').trim();
        const nombre = String(seller?.nombre || '').trim();
        const numCajero = String(seller?.numCajero || '').trim();

        if (!codigo || !nombre || !numCajero) return null;

        return {
            codigo,
            nombre,
            numCajero,
        };
    }

    private normalizePrinter(printer: Partial<FiscalPrinterData> | null | undefined): FiscalPrinterData | null {
        const seller = this.normalizeSeller(printer);
        if (!seller) return null;

        return {
            ...seller,
            active: true,
        };
    }

    /**
     * Arranque: espera usuario y bundle. La primera emisión de `canIssueFiscal$` es false
     * mientras el bundle sigue en null, así que no sirve un `take(1)` de ese stream.
     */
    private async currentPlanAllowsFiscalInvoice(waitMs?: number, gate: 'access' | 'issue' = 'issue'): Promise<boolean> {
        if (!this.plansService.plansBundle$.value) {
            this.plansService.obtainPlansBundle();
        }
        const ready$ = gate === 'access' ? this.fiscalAccess.hasFiscalAccessWhenReady$(waitMs) : this.fiscalAccess.canIssueFiscalWhenReady$(waitMs);
        return firstValueFrom(ready$);
    }

    private matchesConfiguredPrinter(seller: FiscalSellerData, printers: SmartImprFiscalPrinter[]): boolean {
        const activePrinters = printers.filter(printer => printer.enabled !== false);
        if (activePrinters.length === 0) return false;

        return activePrinters.some(printer => String(printer.numCajero || '').trim() === seller.numCajero.trim() && String(printer.codigo || '').trim() === seller.codigo.trim());
    }

    private async openRegistrationModal(configuredPrinters: SmartImprFiscalPrinter[] = []): Promise<FiscalSellerData | null> {
        const modalRef = this.modalService.open(FiscalSellerRegistrationComponent, {
            centered: true,
            backdrop: true,
            keyboard: true,
        });
        modalRef.componentInstance.availablePrinters = configuredPrinters;
        modalRef.componentInstance.requireConfiguredPrinter = true;

        try {
            const seller = await modalRef.result;
            const normalized = this.normalizeSeller(seller);
            if (!normalized) return null;

            this.saveSeller(normalized);
            return normalized;
        } catch {
            return null;
        }
    }

    private getStorageKey(): string {
        const tenantId = this.userService.user$?.value?.tenantId || 'unknown';
        return `${this.STORAGE_KEY_PREFIX}_${tenantId}`;
    }
}
